// Shared canvas-sprite label: a rounded dark pill with gold text, billboarded
// toward the camera. Used by the floor measure tool and the on-wall dimension
// annotations. Dirty-checked so the canvas only re-renders when text changes.
//
// The pill sizes to its text: setLabel re-measures and resizes the canvas WIDTH
// (height is fixed), records the aspect ratio, and keeps the sprite's X-scale =
// Y-scale × aspect so the pill never distorts. Consumers only need to drive the
// HEIGHT (sprite.scale.y); width follows. wall-labels' updateScales multiplies
// in `aspect` for that reason.

import * as THREE from "three";

export interface SpriteLabel {
  sprite: THREE.Sprite;
  texture: THREE.CanvasTexture;
  canvas: HTMLCanvasElement;
  last: string;
  aspect: number; // canvas width / height — the pill's current proportions
}

const CANVAS_H = 64; // fixed texture height; width grows with the text
const FONT = "600 30px system-ui, sans-serif";
const PILL_Y = 10; // rounded-rect top within the canvas
const PILL_H = 44; // rounded-rect height
const PAD_X = 15; // text → pill edge
const MARGIN = 8; // transparent gap around the pill (room for the rounded corners)
const MIN_W = 56; // floor so a 1-char label still reads as a pill

export function makeLabel(
  scaleX = 0.62,
  scaleY = 0.155,
  renderOrder = 12,
): SpriteLabel {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = CANVAS_H;
  const texture = new THREE.CanvasTexture(canvas);
  // The width is now non-power-of-two, so skip mipmaps (NPOT + mipmaps renders
  // black on WebGL1) and just filter linearly — crisp at the pill's on-screen size.
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  const sprite = new THREE.Sprite(
    // toneMapped:false — dimension pills are UI chrome and keep their exact
    // colors under the presentation rig's ACES tone mapping.
    new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true, toneMapped: false }),
  );
  // scaleX is just the initial proportion; setLabel resets it from the text.
  sprite.scale.set(scaleX, scaleY, 1);
  sprite.renderOrder = renderOrder;
  sprite.visible = false;
  return { sprite, texture, canvas, last: "", aspect: canvas.width / canvas.height };
}

export function setLabel(
  l: SpriteLabel,
  text: string,
  textColor = "#ffe87a",
): void {
  if (text === l.last) return;
  const ctx = l.canvas.getContext("2d")!;
  // Measure first (resizing the canvas below resets the context state).
  ctx.font = FONT;
  const textW = ctx.measureText(text).width;
  const pillW = Math.max(MIN_W - 2 * MARGIN, textW + 2 * PAD_X);
  const w = Math.ceil(pillW + 2 * MARGIN);
  const widthChanged = w !== l.canvas.width;
  l.canvas.width = w; // clears + resets the context
  // On WebGL2 the texture is immutable-storage at its first size, so a resize
  // would re-upload into the old (larger) allocation and leave stale pixels to
  // the side. Dispose so it reallocates at the new size.
  if (widthChanged) l.texture.dispose();

  ctx.clearRect(0, 0, w, CANVAS_H);
  ctx.fillStyle = "rgba(20,21,31,0.85)";
  roundRect(ctx, MARGIN, PILL_Y, pillW, PILL_H, 10);
  ctx.fill();
  ctx.fillStyle = textColor;
  ctx.font = FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, w / 2, PILL_Y + PILL_H / 2);

  l.texture.needsUpdate = true;
  l.last = text;
  // Update the pill's proportions and keep the sprite undistorted: width tracks
  // the height the consumer set (measure labels keep makeLabel's; wall labels
  // get theirs in updateScales).
  l.aspect = w / CANVAS_H;
  l.sprite.scale.x = l.sprite.scale.y * l.aspect;
}

function roundRect(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}
